import React, { useState } from 'react';
import './WelfareCheck.css';

const WelfareCheck = ({ serviceId, initialConcern = '', initialAnswers = null, onConcernChange = () => {}, onAnswersChange = () => {} }) => {
  const [immediateRisk, setImmediateRisk] = useState(initialAnswers?.immediateRisk || initialConcern);
  const [immediateConcerns, setImmediateConcerns] = useState(initialAnswers?.immediateConcerns || '');
  const [concerns, setConcerns] = useState((initialAnswers?.concerns || []).map((concern) => concern.id));
  const [concernDetails, setConcernDetails] = useState(Object.fromEntries(
    (initialAnswers?.concerns || []).map((concern) => [concern.id, concern.details || ''])
  ));
  const [followUpAnswers, setFollowUpAnswers] = useState(Object.fromEntries(
    (initialAnswers?.concerns || []).map((concern) => [concern.id, {
      ...(concern.ambulanceCalled ? { ambulanceCalled: concern.ambulanceCalled } : {}),
      ...(concern.emergencyAssistanceRequired ? { emergencyAssistanceRequired: concern.emergencyAssistanceRequired } : {}),
    }])
  ));

  const concernOptions = [
    { id: 'breathing', label: 'Customer is having difficulty breathing', requiresDetails: true, emergency: false },
    { id: 'chestPain', label: 'Customer is complaining of chest pain', requiresDetails: true, emergency: true },
    { id: 'fall', label: 'Customer has had a fall', requiresDetails: true, emergency: false },
    { id: 'pain', label: 'Customer is complaining of pain', requiresDetails: true, emergency: false },
    { id: 'deterioration', label: 'Medical condition has worsened since last visit', requiresDetails: true, emergency: false },
    { id: 'medication', label: 'Not taking medication / adhering to treatment', requiresDetails: true, emergency: false },
    { id: 'skin', label: 'Skin condition appears abnormal', requiresDetails: true, emergency: false },
    { id: 'refusing', label: 'Customer is refusing service', requiresDetails: true, emergency: false },
    { id: 'cognitive', label: 'Incoherent, confused, or cognitive decline', requiresDetails: true, emergency: false },
    { id: 'nutrition', label: 'Customer is not eating or drinking (no food in fridge)', requiresDetails: true, emergency: false },
    { id: 'mobility', label: 'Difficulty moving more than usual', requiresDetails: true, emergency: false },
    { id: 'home', label: 'Residence is in a disorderly state more than usual', requiresDetails: true, emergency: false },
  ];

  const getAnswerSnapshot = (overrides = {}) => {
    const nextImmediateRisk = overrides.immediateRisk ?? immediateRisk;
    const nextImmediateConcerns = overrides.immediateConcerns ?? immediateConcerns;
    const nextConcerns = overrides.concerns ?? concerns;
    const nextConcernDetails = overrides.concernDetails ?? concernDetails;
    const nextFollowUpAnswers = overrides.followUpAnswers ?? followUpAnswers;
    return {
      schemaVersion: 1,
      immediateRisk: nextImmediateRisk,
      immediateConcerns: nextImmediateRisk === 'yes' ? nextImmediateConcerns : '',
      concerns: nextImmediateRisk === 'yes' ? nextConcerns.map((id) => ({
        id,
        details: nextConcernDetails[id] || '',
        ...(nextFollowUpAnswers[id]?.ambulanceCalled ? { ambulanceCalled: nextFollowUpAnswers[id].ambulanceCalled } : {}),
        ...(nextFollowUpAnswers[id]?.emergencyAssistanceRequired ? { emergencyAssistanceRequired: nextFollowUpAnswers[id].emergencyAssistanceRequired } : {}),
      })) : [],
    };
  };

  const handleImmediateRiskChange = (value) => {
    setImmediateRisk(value);
    setImmediateConcerns('');
    setConcerns([]);
    setConcernDetails({});
    setFollowUpAnswers({});
    onConcernChange(value);
    onAnswersChange(getAnswerSnapshot({ immediateRisk: value, immediateConcerns: '', concerns: [], concernDetails: {}, followUpAnswers: {} }));
  };

  const handleImmediateConcernsChange = (value) => {
    setImmediateConcerns(value);
    onAnswersChange(getAnswerSnapshot({ immediateConcerns: value }));
  };

  const handleConcernToggle = (concernId) => {
    if (concerns.includes(concernId)) {
      const nextConcerns = concerns.filter(c => c !== concernId);
      setConcerns(nextConcerns);
      const newDetails = { ...concernDetails };
      delete newDetails[concernId];
      setConcernDetails(newDetails);
      const newFollowUpAnswers = { ...followUpAnswers };
      delete newFollowUpAnswers[concernId];
      setFollowUpAnswers(newFollowUpAnswers);
      onAnswersChange(getAnswerSnapshot({ concerns: nextConcerns, concernDetails: newDetails, followUpAnswers: newFollowUpAnswers }));
    } else {
      const nextConcerns = [...concerns, concernId];
      setConcerns(nextConcerns);
      onAnswersChange(getAnswerSnapshot({ concerns: nextConcerns }));
    }
  };

  const handleDetailChange = (concernId, detail) => {
    const nextDetails = {
      ...concernDetails,
      [concernId]: detail,
    };
    setConcernDetails(nextDetails);
    onAnswersChange(getAnswerSnapshot({ concernDetails: nextDetails }));
  };

  const handleFollowUpChange = (concernId, field, value) => {
    const nextAnswers = {
      ...followUpAnswers,
      [concernId]: { ...(followUpAnswers[concernId] || {}), [field]: value },
    };
    setFollowUpAnswers(nextAnswers);
    onAnswersChange(getAnswerSnapshot({ followUpAnswers: nextAnswers }));
  };

  return (
    <>
      <div className="form-group">
        <label className="form-label required">
          Did you have any immediate and urgent concerns for the customer during your visit today?
        </label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="immediateRisk"
              value="no"
              checked={immediateRisk === 'no'}
              onChange={(e) => handleImmediateRiskChange(e.target.value)}
            />
              No - there were no immediate and urgent concerns
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="immediateRisk"
              value="yes"
              checked={immediateRisk === 'yes'}
              onChange={(e) => handleImmediateRiskChange(e.target.value)}
            />
              Yes - I had immediate and urgent concerns
          </label>
        </div>
      </div>

      {immediateRisk === 'no' && <p className="welfare-complete-message">No immediate concerns reported.</p>}

      {immediateRisk === 'yes' && (
        <div className="welfare-check">
          <div className="welfare-header">
            <h3 className="welfare-title">Welfare Check</h3>
          </div>
          <div className="welfare-section">
            <label className="welfare-label required">
              Immediate concerns?
            </label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="immediate"
                  value="no"
                  checked={immediateConcerns === 'no'}
                  onChange={() => handleImmediateConcernsChange('no')}
                />
                No - I have concerns they are not immediate
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="immediate"
                  value="yes"
                  checked={immediateConcerns === 'yes'}
                  onChange={() => handleImmediateConcernsChange('yes')}
                />
                Yes - I will immediately/have contacted Emergency Services "000"
              </label>
            </div>
          </div>

          {immediateConcerns === 'yes' && (
            <div className="emergency-banner">
              <h3>CRITICAL NOTICE</h3>
              <ul>
                <li>Contact Emergency Services on 000 immediately.</li>
                <li>Complete this form after calling emergency services.</li>
              </ul>
            </div>
          )}

          {immediateConcerns && <div className="welfare-section">
            <label className="welfare-label required">
              Select all concerns that apply:
            </label>
            <div className="concerns-list">
              {concernOptions.map((option) => (
                <div key={option.id} className="concern-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={concerns.includes(option.id)}
                      onChange={() => handleConcernToggle(option.id)}
                    />
                    <span>{option.label}</span>
                  </label>

                  {concerns.includes(option.id) && option.requiresDetails && (
                    <div className="concern-details">
                      {option.id === 'breathing' && (
                        <div className="detail-group">
                          <label className="detail-label">Was an ambulance called?</label>
                          <div className="radio-group-inline">
                            <label>
                              <input type="radio" name={`${option.id}-ambulance`} value="yes" checked={followUpAnswers[option.id]?.ambulanceCalled === 'yes'} onChange={() => handleFollowUpChange(option.id, 'ambulanceCalled', 'yes')} />
                              Yes
                            </label>
                            <label>
                              <input type="radio" name={`${option.id}-ambulance`} value="no" checked={followUpAnswers[option.id]?.ambulanceCalled === 'no'} onChange={() => handleFollowUpChange(option.id, 'ambulanceCalled', 'no')} />
                              No
                            </label>
                          </div>
                          <p className="detail-hint">Consider calling ambulance if not normal breathing condition and is severe</p>
                        </div>
                      )}

                      {option.id === 'chestPain' && (
                        <div className="detail-group">
                          <label className="detail-label">Is emergency assistance required?</label>
                          <div className="radio-group-inline">
                            <label>
                              <input type="radio" name={`${option.id}-emergency`} value="yes" checked={followUpAnswers[option.id]?.emergencyAssistanceRequired === 'yes'} onChange={() => handleFollowUpChange(option.id, 'emergencyAssistanceRequired', 'yes')} />
                              Yes
                            </label>
                            <label>
                              <input type="radio" name={`${option.id}-emergency`} value="no" checked={followUpAnswers[option.id]?.emergencyAssistanceRequired === 'no'} onChange={() => handleFollowUpChange(option.id, 'emergencyAssistanceRequired', 'no')} />
                              No
                            </label>
                          </div>
                        </div>
                      )}

                      <label className="detail-label">Describe the concern:</label>
                      <textarea
                        className="detail-textarea"
                        value={concernDetails[option.id] || ''}
                        onChange={(e) => handleDetailChange(option.id, e.target.value)}
                        placeholder={option.id === 'fall'
                          ? 'Describe the injury'
                          : option.id === 'pain'
                            ? 'Describe pain, if acute call an ambulance'
                            : option.id === 'deterioration'
                              ? 'Provide notes of how the customers medical condition has worsened since the last visit'
                              : option.id === 'medication'
                                ? 'Provide details about customer not taking their medications or adhering to treatment'
                                : 'Provide details...'}
                        rows="3"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>}
        </div>
      )}
    </>
  );
};

export default WelfareCheck;
